<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-content">
          <v-icon class="header-icon" size="32">mdi-bell-ring</v-icon>
          <h3 class="header-title">Notifications</h3>
          <span v-if="unreadCount > 0" class="unread-badge">
            {{ unreadCount }}
          </span>
        </div>
        <div class="header-actions">
          <v-btn
            v-if="notifications.length > 0 && unreadCount > 0"
            @click="markAllAsRead"
            variant="outlined"
            prepend-icon="mdi-check-all"
            size="small"
            class="action-btn-header"
          >
            Mark All Read
          </v-btn>
          <button @click="$emit('close')" class="btn-close">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
      </div>

      <div class="modal-body">
        <!-- Loading -->
        <div v-if="loading" class="loading-screen">
          <div class="spinner"></div>
          <p class="loading-text">Loading notifications...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="notifications.length === 0" class="empty-state">
          <v-icon size="80" color="grey">mdi-bell-outline</v-icon>
          <h4 class="empty-title">No Notifications</h4>
          <p class="empty-text">You'll see new message notifications here</p>
        </div>

        <!-- Notifications List -->
        <div v-else class="notifications-list">
          <div 
            v-for="notification in notifications"
            :key="notification._id"
            @click="handleNotificationClick(notification)"
            class="notification-item"
            :class="{
              unread: !notification.read,
              'new-message': notification.type === 'new_message'
            }"
          >
            <!-- Unread indicator -->
            <div v-if="!notification.read" class="unread-indicator"></div>

            <!-- Notification Icon -->
            <div class="notification-icon" :class="`icon-${notification.type}`">
              <v-icon v-if="notification.type === 'new_message'" size="20">
                mdi-message-text
              </v-icon>
              <v-icon v-else-if="notification.type === 'user_added'" size="20">
                mdi-account-plus
              </v-icon>
              <v-icon v-else-if="notification.type === 'user_removed'" size="20">
                mdi-account-minus
              </v-icon>
              <v-icon v-else-if="notification.type === 'chat_deleted'" size="20">
                mdi-delete
              </v-icon>
              <v-icon v-else size="20">mdi-bell</v-icon>
            </div>

            <!-- Notification Content -->
            <div class="notification-content">
              <div class="notification-header">
                <div class="notification-title-wrapper">
                  <span class="notification-title">
                    {{ getNotificationTitle(notification) }}
                  </span>
                  <v-chip
                    v-if="notification.type === 'new_message' && notification.messageCount > 1"
                    size="x-small"
                    color="primary"
                    class="message-count-chip"
                  >
                    +{{ notification.messageCount - 1 }}
                  </v-chip>
                </div>
                <span class="notification-time">
                  {{ formatTime(notification.createdAt) }}
                </span>
              </div>
              
              <div class="notification-body">
                <div v-if="notification.type === 'new_message'" class="message-notification">
                  <div class="sender-name">{{ notification.senderName }}</div>
                  <div class="message-preview">{{ notification.messagePreview }}</div>
                </div>
                
                <div v-else class="text-notification">
                  {{ getNotificationDescription(notification) }}
                </div>
              </div>
            </div>

            <!-- Notification Actions -->
            <div class="notification-actions">
              <button 
                v-if="!notification.read"
                @click.stop="markAsRead(notification._id)"
                class="btn-action"
                title="Mark as read"
              >
                <v-icon size="16">mdi-check</v-icon>
              </button>
              <button 
                @click.stop="deleteNotification(notification._id)"
                class="btn-action btn-delete"
                title="Delete"
              >
                <v-icon size="16">mdi-delete</v-icon>
              </button>
            </div>
          </div>

          <!-- Load More -->
          <div v-if="hasMore && !loadingMore" class="load-more-section">
            <v-btn
              @click="loadMore"
              variant="outlined"
              prepend-icon="mdi-chevron-down"
              class="load-more-btn"
              block
            >
              Load More
            </v-btn>
          </div>

          <!-- Loading More -->
          <div v-if="loadingMore" class="loading-more">
            <div class="spinner-small"></div>
            <span>Loading more...</span>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer">
        <v-btn
          v-if="notifications.length > 0"
          @click="deleteAllNotifications"
          variant="outlined"
          prepend-icon="mdi-delete-sweep"
          color="error"
          class="footer-btn"
        >
          Clear All
        </v-btn>
        <v-spacer v-if="notifications.length > 0"></v-spacer>
        <v-btn
          @click="$emit('close')"
          variant="outlined"
          prepend-icon="mdi-close"
          class="footer-btn"
        >
          Close
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import chatService from '@/services/chat/ChatService'
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useMobile } from '@/composables/useMobile.js'
import { useMobileXs } from '@/composables/useMobileXs.js'

export default {
  setup() {
    const isMobile = useMobile()
    const isMobileXs = useMobileXs()

    const notify = (message) => {
      toast(message, {
        theme: "dark",
        type: "success",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 1500,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    const notifyError = (message) => {
      toast(message, {
        theme: "dark",
        type: "error",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 3000,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    const notifyWarning = (message) => {
      toast(message, {
        theme: "dark",
        type: "warning",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 3000,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    return { isMobile, isMobileXs, notify, notifyError, notifyWarning }
  },
  name: 'NotificationsModal',
  emits: ['close'],
  data() {
    return {
      notifications: [],
      loading: true,
      loadingMore: false,
      currentPage: 1,
      hasMore: false,
      unreadCount: 0
    }
  },
  async mounted() {
    await this.loadNotifications()
  },
  methods: {
    closeModal() {
      this.$emit('close')
    },

    async loadNotifications(page = 1) {
      try {
        if (page === 1) {
          this.loading = true
        } else {
          this.loadingMore = true
        }

        const response = await chatService.getNotifications(page, 3)
        
        if (page === 1) {
          this.notifications = response.notifications
        } else {
          this.notifications.push(...response.notifications)
        }

        this.currentPage = page
        this.hasMore = response.pagination.hasMore
        this.unreadCount = response.unreadCount

      } catch (error) {
        this.showToast('Failed to load notifications', 'error')
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    async loadMore() {
      if (!this.hasMore || this.loadingMore) return
      await this.loadNotifications(this.currentPage + 1)
    },

    async markAsRead(notificationId) {
      try {
        await chatService.markNotificationsAsRead([notificationId])
        const notification = this.notifications.find(n => n._id === notificationId)
        if (notification && !notification.read) {
          notification.read = true
          this.unreadCount = Math.max(0, this.unreadCount - 1)
        }

      } catch (error) {
        this.showToast('Failed to mark as read', 'error')
      }
    },

    async markAllAsRead() {
      if (this.unreadCount === 0) return

      try {
        await chatService.markNotificationsAsRead()
        this.notifications.forEach(notification => {
          notification.read = true
        })
        this.unreadCount = 0

        this.showToast('All notifications marked as read', 'success')

      } catch (error) {
        this.showToast('Failed to mark all as read', 'error')
      }
    },

    async deleteNotification(notificationId) {
      try {
        const notification = this.notifications.find(n => n._id === notificationId)
        await chatService.deleteNotification(notificationId)
        this.notifications = this.notifications.filter(n => n._id !== notificationId)
        if (notification && !notification.read) {
          this.unreadCount = Math.max(0, this.unreadCount - 1)
        }
        this.showToast('Notification deleted', 'success')

      } catch (error) {
        this.showToast('Failed to delete notification', 'error')
      }
    },

    async deleteAllNotifications() {
      try {
        await chatService.deleteAllNotifications()
        
        this.notifications = []
        this.unreadCount = 0
        
        this.showToast('All notifications deleted', 'success')

      } catch (error) {
        this.showToast('Failed to delete all notifications', 'error')
      }
    },

    handleNotificationClick(notification) {
      // Mark as read if unread
      if (!notification.read) {
        this.markAsRead(notification._id)
      }

      // Navigate to chat if it's a message notification
      if (notification.type === 'new_message' && notification.chatId) {
        this.$emit('close')
        chatService.gotoChat(notification)
      }
    },

    getNotificationTitle(notification) {
      switch (notification.type) {
        case 'new_message':
          return 'New Message'
        case 'user_added':
          return 'Added to Chat'
        case 'user_removed':
          return 'Removed from Chat'
        case 'chat_deleted':
          return 'Chat Deleted'
        default:
          return 'Notification'
      }
    },

    getNotificationDescription(notification) {
      switch (notification.type) {
        case 'user_added':
          return `You were added to a chat by ${notification.senderName}`
        case 'user_removed':
          return `You were removed from a chat by ${notification.senderName}`
        case 'chat_deleted':
          return `A chat was deleted by ${notification.senderName}`
        default:
          return notification.message || 'You have a new notification'
      }
    },

    formatTime(timestamp) {
      const date = new Date(timestamp)
      const now = new Date()
      
      // Same day - show time only
      if (date.toDateString() === now.toDateString()) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
      
      // Yesterday
      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)
      if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday'
      }
      
      // This week
      const weekAgo = new Date(now)
      weekAgo.setDate(weekAgo.getDate() - 7)
      if (date > weekAgo) {
        return date.toLocaleDateString([], { weekday: 'short' })
      }
      
      // Older
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    },

    showToast(message, type = 'info') {
      if (type === 'error') {
        this.notifyError(message)
      } else if (type === 'warning'){
        this.notifyWarning(message)
      } else {
        this.notify(message)
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@use '../../views/css/styles.scss';

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

/* Modal Content */
.modal-content {
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.98), rgba(18, 18, 18, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;
}

/* Modal Header */
.modal-header {
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0.3);
  gap: 16px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.header-icon {
  color: #ffc107 !important;
  animation: bellRing 2s ease-in-out infinite;
}

@keyframes bellRing {
  0%, 100% {
    transform: rotate(0deg);
  }
  10%, 30% {
    transform: rotate(-15deg);
  }
  20%, 40% {
    transform: rotate(15deg);
  }
}

.header-title {
  margin: 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: white;
}

.unread-badge {
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 12px;
  font-family: "Darker Grotesque", sans-serif;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.action-btn-header {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.action-btn-header:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

.btn-close {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: white;
}

.btn-close:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
}

/* Modal Body */
.modal-body {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: transparent;
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

/* Loading Screen */
.loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.loading-text {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 16px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-title {
  margin: 20px 0 10px 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: white;
}

.empty-text {
  margin: 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.6;
}

/* Notifications List */
.notifications-list {
  display: flex;
  flex-direction: column;
}

/* Notification Item */
.notification-item {
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  position: relative;
  animation: slideIn 0.3s ease;
}

.notification-item:hover {
  background: rgba(0, 123, 255, 0.1);
}

.notification-item.unread {
  background: rgba(0, 123, 255, 0.08);
  border-left: 3px solid #007bff;
}

.notification-item.unread:hover {
  background: rgba(0, 123, 255, 0.15);
}

/* Unread Indicator */
.unread-indicator {
  position: absolute;
  top: 24px;
  left: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #007bff;
  box-shadow: 0 0 8px rgba(0, 123, 255, 0.6);
  animation: pulse 2s ease-in-out infinite;
}

/* Notification Icon */
.notification-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.icon-new_message {
  background: linear-gradient(135deg, #007bff, #0056b3);
}

.icon-user_added {
  background: linear-gradient(135deg, #28a745, #1e7e34);
}

.icon-user_removed {
  background: linear-gradient(135deg, #dc3545, #c82333);
}

.icon-chat_deleted {
  background: linear-gradient(135deg, #6c757d, #545b62);
}

.notification-item:hover .notification-icon {
  transform: scale(1.05);
}

/* Notification Content */
.notification-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.notification-title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-family: "Darker Grotesque", sans-serif;
  font-weight: 600;
  color: white;
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-count-chip {
  font-family: "Darker Grotesque", sans-serif !important;
  font-weight: 700 !important;
}

.notification-time {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
}

/* Notification Body */
.notification-body {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
}

.message-notification {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sender-name {
  font-weight: 600;
  color: #007bff;
}

.message-preview {
  color: rgba(255, 255, 255, 0.7);
  word-break: break-word;
  line-height: 1.5;
}

.text-notification {
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
}

/* Notification Actions */
.notification-actions {
  display: flex;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
}

.notification-item:hover .notification-actions {
  opacity: 1;
}

.btn-action {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-action:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
  transform: translateY(-2px);
}

.btn-delete:hover {
  background: rgba(220, 53, 69, 0.2);
  border-color: #dc3545;
  color: #dc3545;
}

/* Load More Section */
.load-more-section {
  padding: 20px;
  text-align: center;
}

.load-more-btn {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.load-more-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

/* Loading More */
.loading-more {
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Modal Footer */
.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 12px;
  background: rgba(0, 0, 0, 0.3);
}

.footer-btn {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.footer-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-height: 85vh;
  }

  .modal-header {
    padding: 20px;
    flex-wrap: wrap;
  }

  .header-content {
    flex: 1 1 100%;
    margin-bottom: 12px;
  }

  .header-actions {
    flex: 1 1 100%;
    justify-content: space-between;
  }

  .header-title {
    font-size: 20px;
  }

  .notification-item {
    padding: 14px 16px;
    gap: 12px;
  }

  .notification-icon {
    width: 36px;
    height: 36px;
  }

  .notification-actions {
    opacity: 1;
  }

  .modal-footer {
    padding: 16px 20px;
    flex-direction: column-reverse;

    .footer-btn {
      width: 100%;
    }
  }
}

/* Vuetify Overrides */
:deep(.v-btn:hover) > .v-btn__overlay {
  opacity: 0.1;
}

:deep(.v-chip) {
  font-family: "Darker Grotesque", sans-serif !important;
}
</style>